import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Card, Grid, Progress, Statistic, Table, Tag, Typography } from '@arco-design/web-react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { OverviewPageHeader } from '@/components-biz/OverviewPageHeader';

import styles from './index.module.less';

const { Row, Col } = Grid;

/** 算力总览展示视角。 */
export type ComputeCenterOverviewScope = 'data-center' | 'tenant';

interface ComputeCenterOverviewProps {
  /** 数据中心展示供给侧数据，租户空间展示配额与使用数据。 */
  scope: ComputeCenterOverviewScope;
}

interface RecentResource {
  key: string;
  name: string;
  type: string;
  specification: string;
  status: 'running' | 'stopped';
  createdAt: string;
}

const TREND = [
  { date: '07-25', cpu: 48, gpu: 42, memory: 53 },
  { date: '07-26', cpu: 52, gpu: 47, memory: 56 },
  { date: '07-27', cpu: 57, gpu: 51, memory: 58 },
  { date: '07-28', cpu: 54, gpu: 59, memory: 61 },
  { date: '07-29', cpu: 63, gpu: 62, memory: 64 },
  { date: '07-30', cpu: 68, gpu: 66, memory: 67 },
  { date: '07-31', cpu: 72, gpu: 64, memory: 69 },
];

const MOCK_DATA = {
  'data-center': {
    metrics: [
      ['hooks:compute.overview.cpu.cores', 1280, 'Core', '+4.2%'],
      ['hooks:compute.overview.gpu.cards', 192, 'GPU', '+2.1%'],
      ['hooks:compute.overview.memory.capacity', 4096, 'GB', '+3.8%'],
      ['hooks:compute.overview.storage.capacity', 860, 'TB', '+6.5%'],
      ['hooks:compute.overview.running.instances', 326, '', '+18'],
      ['hooks:compute.overview.compute.clusters', 8, '', '100%'],
      ['hooks:compute.overview.compute.nodes', 124, '', '95.2%'],
      ['hooks:compute.overview.available.ip', 1846, '', '-3.1%'],
    ],
    resources: [
      ['hooks:compute.overview.general.compute', 72],
      ['hooks:compute.overview.gpu.compute', 64],
      ['hooks:compute.overview.bare.metal.compute', 48],
      ['hooks:compute.overview.container.compute', 58],
    ],
    distribution: [
      ['hooks:general.container', 186, '#165dff'],
      ['cluster:cloud-instance', 96, '#14c9c9'],
      ['hooks:instance.bare.metal', 44, '#f7ba1e'],
    ],
    risks: [
      ['hooks:compute.overview.risk.gpu.pool', 86, 'orange'],
      ['hooks:compute.overview.risk.storage.pool', 81, 'orange'],
      ['hooks:compute.overview.risk.node', 3, 'red'],
    ],
    riskSummary: [3, 96.8],
    instanceSummary: [340, 326, 14],
    recent: [
      ['dc-1', 'training-gpu-021', 'hooks:general.container', '8 GPU · 64 Core · 256 GB', 'running', '2026-07-31 10:24'],
      ['dc-2', 'inference-vm-118', 'cluster:cloud-instance', '16 Core · 64 GB', 'running', '2026-07-31 09:18'],
      ['dc-3', 'bm-gpu-h100-07', 'hooks:instance.bare.metal', '8 H100 · 2 TB', 'stopped', '2026-07-30 18:42'],
    ],
  },
  tenant: {
    metrics: [
      ['hooks:compute.overview.cpu.cores', 156, 'Core', '61%'],
      ['hooks:compute.overview.gpu.cards', 24, 'GPU', '50%'],
      ['hooks:compute.overview.memory.capacity', 512, 'GB', '68%'],
      ['hooks:compute.overview.storage.capacity', 42, 'TB', '43%'],
      ['hooks:compute.overview.running.instances', 38, '', '+4'],
      ['hooks:compute.overview.total.instances', 46, '', '+6'],
      ['hooks:compute.overview.stopped.instances', 8, '', '+2'],
      ['hooks:compute.overview.available.ip', 36, '', '-4'],
    ],
    resources: [
      ['cluster:cpu-quota', 61],
      ['cluster:gpu-quota', 50],
      ['cluster:memory-quota', 68],
      ['hooks:compute.overview.storage.quota', 43],
    ],
    distribution: [
      ['hooks:general.container', 22, '#165dff'],
      ['cluster:cloud-instance', 17, '#14c9c9'],
      ['hooks:instance.bare.metal', 7, '#f7ba1e'],
    ],
    risks: [
      ['hooks:compute.overview.risk.memory.quota', 88, 'orange'],
      ['hooks:compute.overview.risk.gpu.quota', 83, 'orange'],
      ['hooks:compute.overview.risk.instance', 2, 'red'],
    ],
    riskSummary: [3, 95.7],
    instanceSummary: [46, 38, 8],
    recent: [
      ['ws-1', 'llm-training-06', 'hooks:general.container', '4 GPU · 32 Core · 128 GB', 'running', '2026-07-31 11:06'],
      ['ws-2', 'dev-cloud-12', 'cluster:cloud-instance', '8 Core · 32 GB', 'running', '2026-07-31 08:55'],
      ['ws-3', 'render-bm-02', 'hooks:instance.bare.metal', '2 GPU · 256 GB', 'stopped', '2026-07-30 16:20'],
    ],
  },
} as const;

/** 使用前端模拟数据展示数据中心或租户空间的算力总览。 */
export function ComputeCenterOverview({ scope }: ComputeCenterOverviewProps) {
  const { t } = useTranslation();
  const data = MOCK_DATA[scope];
  const recentResources = data.recent.map(([key, name, type, specification, status, createdAt]) => ({
    key,
    name,
    type,
    specification,
    status,
    createdAt,
  })) as RecentResource[];
  const columns = useMemo(
    () => [
      { title: t('data-center:maas.instance.name'), dataIndex: 'name' },
      { title: t('common:resource.type'), dataIndex: 'type', render: (value: string) => t(value) },
      { title: t('common:resource.configuration'), dataIndex: 'specification' },
      {
        title: t('common:status'),
        dataIndex: 'status',
        render: (value: RecentResource['status']) => (
          <Badge status={value === 'running' ? 'success' : 'default'} text={t(`common:${value}`)} />
        ),
      },
      { title: t('common:created-at'), dataIndex: 'createdAt' },
    ],
    [t],
  );

  return (
    <div className={styles.pageContent}>
      <OverviewPageHeader
        title={t(scope === 'data-center' ? 'hooks:compute.overview.data.center' : 'hooks:compute.overview.tenant')}
        description={t('hooks:compute.overview.description.compute')}
      />

      <Row className={styles.metricsRow} gutter={12}>
        {data.metrics.map(([label, value, suffix, change]) => (
          <Col key={label} xs={24} sm={12} xl={6}>
            <Card className={styles.metricCard} bordered={false}>
              <Statistic title={t(label)} value={value} suffix={suffix} />
              <Typography.Text type="secondary">
                {t('hooks:compute.overview.vs.yesterday')}{' '}
                <span className={change.startsWith('+') ? styles.positiveChange : undefined}>{change}</span>
              </Typography.Text>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className={styles.section} gutter={12}>
        <Col xs={24} xl={16}>
          <Card className={`${styles.card} ${styles.fillCard}`} title={t('hooks:compute.overview.utilization.trend')} bordered={false}>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={TREND}>
                <CartesianGrid stroke="var(--color-border-2)" vertical={false} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} unit="%" axisLine={false} tickLine={false} />
                <Tooltip />
                <Legend />
                <Line
                  name={t('situation-awareness:cpu.usage.percent')}
                  dataKey="cpu"
                  stroke="#165dff"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  name={t('situation-awareness:gpu.usage.percent')}
                  dataKey="gpu"
                  stroke="#f7ba1e"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  name={t('situation-awareness:memory.usage.percent')}
                  dataKey="memory"
                  stroke="#14c9c9"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card className={`${styles.card} ${styles.fillCard}`} title={t('hooks:compute.overview.capacity.risk')} bordered={false}>
            <Row className={styles.summaryDivider} gutter={12}>
              <Col span={12}>
                <Statistic
                  title={t('hooks:compute.overview.risk.items')}
                  value={data.riskSummary[0]}
                  suffix={t('hooks:compute.overview.items.unit')}
                />
              </Col>
              <Col span={12}>
                <Statistic title={t('hooks:compute.overview.health.rate')} value={data.riskSummary[1]} suffix="%" />
              </Col>
            </Row>
            {data.risks.map(([label, value, color]) => (
              <div className={styles.riskItem} key={label}>
                <div className={styles.itemHeader}>
                  <span>{t(label)}</span>
                  <Tag color={color}>
                    {value}
                    {typeof value === 'number' && value > 10 ? '%' : ''}
                  </Tag>
                </div>
                {value > 10 ? <Progress percent={value} showText={false} status={value >= 85 ? 'warning' : 'normal'} /> : null}
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      <Row className={styles.section} gutter={12}>
        <Col xs={24} xl={12}>
          <Card className={`${styles.card} ${styles.fillCard}`} title={t('hooks:compute.overview.resource.usage')} bordered={false}>
            {data.resources.map(([label, percent]) => (
              <div className={styles.resourceItem} key={label}>
                <div className={styles.itemHeader}>
                  <span>{t(label)}</span>
                  <span>{percent}%</span>
                </div>
                <Progress percent={percent} showText={false} />
              </div>
            ))}
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card className={`${styles.card} ${styles.fillCard}`} title={t('hooks:compute.overview.instance.distribution')} bordered={false}>
            <Row className={styles.summaryDivider} gutter={12}>
              {[
                ['hooks:compute.overview.total.instances', data.instanceSummary[0]],
                ['hooks:compute.overview.running.instances', data.instanceSummary[1]],
                ['hooks:compute.overview.stopped.instances', data.instanceSummary[2]],
              ].map(([label, value]) => (
                <Col span={8} key={label}>
                  <Statistic title={t(label as string)} value={value} />
                </Col>
              ))}
            </Row>
            {data.distribution.map(([label, count, color]) => (
              <div className={styles.distributionItem} key={label}>
                <div className={styles.distributionHeader}>
                  <Badge color={color} />
                  <span className={styles.distributionLabel}>{t(label)}</span>
                  <span>
                    {count} {t('hooks:compute.overview.instances.unit')}
                  </span>
                  <Typography.Text className={styles.distributionPercent} type="secondary">
                    {Math.round((count / data.instanceSummary[0]) * 100)}%
                  </Typography.Text>
                </div>
                <Progress percent={Math.round((count / data.instanceSummary[0]) * 100)} color={color} showText={false} size="small" />
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      <Card className={`${styles.card} ${styles.section}`} title={t('hooks:compute.overview.recent.resources')} bordered={false}>
        <Table columns={columns} data={recentResources} pagination={false} />
      </Card>
    </div>
  );
}
